import { useMsal } from '@azure/msal-react';
import { Button } from '@fluentui/react-components';
import { addDays, addMinutes, differenceInHours, format } from 'date-fns';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useCommonStyles, useStores } from '../../common/hooks';
import { EmailTemplateAttachment } from '../../models';
import { EmailPageRouteStates } from '../../models/route-states';
import { RoutePaths } from '../../route-paths';
import { CustomMessageBar, PageTitle, ValidationMessageList } from '../shared-components';
import { FlexBox } from '../shared-components/flex-box';
import { FormInput } from '../shared-components/form/FormInput';
import Loading from '../shared-components/Loading';
import {
  AttachmentsSection,
  BannerImageSection,
  BodyMessageSection,
  IndividualRecipientInput,
  RecipientGroupDropdownInput,
  SendEmailConfirmationModal,
  SendOnSection
} from './components';
import './CreateOrEditEmailTemplatePage.scss';

const CreateOrEditEmailTemplatePageImpl: React.FC = () => {
  const {
    instance
  } = useMsal();

  const {
    apiStore,
    awardStore,
    timeZoneStore,
    createOrEditEmailTemplatePageUiStore,
  } = useStores();

  const commonStyles = useCommonStyles();

  const [isLoading, setLoading] = useState<boolean>(false);

  const [createOrEditEmailTemplateErrorMessage, setCreateOrEditEmailTemplateErrorMessage] = useState<string>();
  const [isShowSendEmailModal, setIsShowSendEmailModal] = useState<boolean>(false);

  const navigate = useNavigate();
  const { emailTemplateId } = useParams() as { emailTemplateId?: string };

  const getParamAndLoadEmailTemplate = async (): Promise<void> => {
    if (emailTemplateId && awardStore.defaultProgramRef) {
      const existingEmailTemplate = await apiStore.getEmailTemplate(instance, awardStore.defaultProgramRef.programId, +emailTemplateId);
      createOrEditEmailTemplatePageUiStore.createEmailTemplateRequestBodyForUpdate(existingEmailTemplate);
    } else {
      createOrEditEmailTemplatePageUiStore.createEmailTemplateRequestBody();
    }
  };

  const validateDateTimeToSend = (selectedDateTime?: string, selectedTimeZone?: string): void => {
    if (createOrEditEmailTemplatePageUiStore.emailTemplateRequestBody?.isSendNow) {
      createOrEditEmailTemplatePageUiStore.setDateTimeValidationMessage(undefined);
      createOrEditEmailTemplatePageUiStore.setDateTimeToSendValid(true);
      return;
    };
    const timeZone = timeZoneStore.timeZones.find((tz) => tz.displayName === selectedTimeZone);
    const selectedDate = new Date(`${selectedDateTime && format(new Date(selectedDateTime), "yyyy-MM-dd'T'HH:mm:ss")}${timeZone?.utcOffset}`);
    const now = new Date();
    if (selectedDate < now) {
      createOrEditEmailTemplatePageUiStore.setDateTimeValidationMessage('Date and time must be after the current time.');
      createOrEditEmailTemplatePageUiStore.setDateTimeToSendValid(false);
    } else if (differenceInHours(selectedDate, addDays(now, 2)) > 48) {
      createOrEditEmailTemplatePageUiStore.setDateTimeValidationMessage('Date and time must not more than 48hours from now.');
      createOrEditEmailTemplatePageUiStore.setDateTimeToSendValid(false);
    } else if (selectedDate <= addMinutes(now, 5)) {
      createOrEditEmailTemplatePageUiStore.setDateTimeValidationMessage('Date and time must be at least 5 minutes after the current time.');
      createOrEditEmailTemplatePageUiStore.setDateTimeToSendValid(false);
    } else {
      createOrEditEmailTemplatePageUiStore.setDateTimeValidationMessage(undefined);
      createOrEditEmailTemplatePageUiStore.setDateTimeToSendValid(true);
    };
  };

  const bytesToMegaBytes = (bytes: number) => bytes / (1024 * 1024);

  const validateAttachmentSizeToSend = (selectedAttachments: File[], existingAttachments: EmailTemplateAttachment[]): void => {
    var maxSizeInMegaBytes = bytesToMegaBytes(20971520);
    var totalSelectedAttachmentSizeInMegaBytes = bytesToMegaBytes(selectedAttachments.reduce<number>((previousFileSize, currentFile) => {
      return previousFileSize + currentFile.size
    }, 0));
    var totalExistingAttachmentSizeInMegaBytes = bytesToMegaBytes(existingAttachments.reduce<number>((previousFileSize, currentFile) => {
      return previousFileSize + currentFile.fileSize
    }, 0));
    var totalSizeInMB = totalSelectedAttachmentSizeInMegaBytes + totalExistingAttachmentSizeInMegaBytes;
    if (totalSizeInMB > maxSizeInMegaBytes) {
      createOrEditEmailTemplatePageUiStore.setAttachmentTotalSizeValidationMessage(`Total attachments size exceeded current limit. Current Size : ${totalSizeInMB} MB Limit : 20 MB`);
      createOrEditEmailTemplatePageUiStore.setIsAttachmentTotalSizeToSendValid(false);
    } else {
      createOrEditEmailTemplatePageUiStore.setAttachmentTotalSizeValidationMessage(undefined);
      createOrEditEmailTemplatePageUiStore.setIsAttachmentTotalSizeToSendValid(true);
    }
  };

  const uploadEmailTemplateBannerImage = async () => {
    if (!createOrEditEmailTemplatePageUiStore.selectedBannerImageFile) return;
    if (!awardStore.defaultProgramRef) return;
    try {
      const bannerImageUrl = await apiStore.uploadEmailTemplateBannerImage(instance, awardStore.defaultProgramRef.programId, createOrEditEmailTemplatePageUiStore.selectedBannerImageFile);
      createOrEditEmailTemplatePageUiStore.setEmailTemplateRequestBody<string>({
        key: 'bannerImageUrl',
        value: bannerImageUrl
      });
    } catch (error) {
      // TODO - Handle error
    }
  };

  const uploadEmailTemplateAttachments = async (emailTemplateId: number) => {
    if (!createOrEditEmailTemplatePageUiStore.selectedAttachments.length) return;
    const programId = awardStore.defaultProgramRef?.programId;
    if (!programId) return;

    const promiseEmailTemplateAttachments = createOrEditEmailTemplatePageUiStore.selectedAttachments.map((attachmentFile) => apiStore.uploadEmailTemplateAttachment(
      instance,
      programId,
      emailTemplateId,
      attachmentFile
    ));

    const emailTemplateAttachments = await Promise.all(promiseEmailTemplateAttachments);
    createOrEditEmailTemplatePageUiStore.setAttachments(emailTemplateAttachments);
  };

  const upsertEmailTemplateAttachments = async (emailTemplateId: number) => {
    if (!awardStore.defaultProgramRef?.programId) return;
    await apiStore.upsertEmailTemplateAttachments(instance, awardStore.defaultProgramRef?.programId, emailTemplateId, createOrEditEmailTemplatePageUiStore.emailTemplateAttachments);
  };

  const onCancelButtonClicked = () => {
    navigate(`${RoutePaths.Email}`, { replace: true });
  };

  const onSaveAsTemplateButtonClicked = async () => {
    createOrEditEmailTemplatePageUiStore.setIsForSending(false);
    createOrEditEmailTemplatePageUiStore.setIsBeingValidated(true);
    createOrEditEmailTemplatePageUiStore.setDateTimeValidationMessage(undefined);
    createOrEditEmailTemplatePageUiStore.setDateTimeToSendValid(true);
    validateAttachmentSizeToSend(
      createOrEditEmailTemplatePageUiStore.selectedAttachments,
      createOrEditEmailTemplatePageUiStore.emailTemplateAttachments
    );
    if (!createOrEditEmailTemplatePageUiStore.emailTemplateRequestBody
      || !createOrEditEmailTemplatePageUiStore.emailTemplateRequestValidationForSaving.isValid
      || !createOrEditEmailTemplatePageUiStore.isAttachmentTotalSizeToSendValid) return;
    setLoading(true);
    try {
      if (emailTemplateId && awardStore.defaultProgramRef) {
        await uploadEmailTemplateBannerImage();
        await apiStore.updateEmailTemplate(
          instance,
          awardStore.defaultProgramRef.programId,
          +emailTemplateId,
          createOrEditEmailTemplatePageUiStore.emailTemplateRequestBody,
          false);
        await uploadEmailTemplateAttachments(+emailTemplateId);
        await upsertEmailTemplateAttachments(+emailTemplateId);
        navigate(`${RoutePaths.Email}`, {
          state: {
            operationType: 'update',
          } as Required<EmailPageRouteStates>
        });
      } else {
        if (!awardStore.defaultProgramRef) return;
        await uploadEmailTemplateBannerImage();
        const newEmailTemplateId = await apiStore.createEmailTemplate(
          instance,
          awardStore.defaultProgramRef.programId,
          createOrEditEmailTemplatePageUiStore.emailTemplateRequestBody,
          false,
          false);
        await uploadEmailTemplateAttachments(newEmailTemplateId);
        await upsertEmailTemplateAttachments(newEmailTemplateId);
        navigate(`${RoutePaths.Email}`, {
          state: {
            operationType: 'create',
          } as Required<EmailPageRouteStates>
        });
      }
    } catch (error) {
      if (emailTemplateId) {
        setCreateOrEditEmailTemplateErrorMessage('Fail to update an email template.');
      } else {
        setCreateOrEditEmailTemplateErrorMessage('Fail to create an email template.');
      }
    } finally {
      setLoading(false);
    }
  };

  const onSendButtonClicked = () => {
    createOrEditEmailTemplatePageUiStore.setIsForSending(true);
    createOrEditEmailTemplatePageUiStore.setIsBeingValidated(true);
    validateDateTimeToSend(
      createOrEditEmailTemplatePageUiStore.emailTemplateRequestBody?.sendOnDateTime ?? undefined,
      createOrEditEmailTemplatePageUiStore.emailTemplateRequestBody?.sendOnTimeZone ?? undefined,
    );
    validateAttachmentSizeToSend(
      createOrEditEmailTemplatePageUiStore.selectedAttachments,
      createOrEditEmailTemplatePageUiStore.emailTemplateAttachments
    );
    if (!createOrEditEmailTemplatePageUiStore.emailTemplateRequestValidationForSending.isValid
      || !createOrEditEmailTemplatePageUiStore.isDateTimeToSendValid
      || !createOrEditEmailTemplatePageUiStore.isAttachmentTotalSizeToSendValid) return;
    setIsShowSendEmailModal(true);
  };

  const onConfirmSendEmailButtonClicked = async () => {
    setIsShowSendEmailModal(false);
    setLoading(true);
    if (!createOrEditEmailTemplatePageUiStore.emailTemplateRequestBody) return;
    try {
      if (emailTemplateId && awardStore.defaultProgramRef) {
        await uploadEmailTemplateBannerImage();
        await apiStore.updateEmailTemplate(
          instance,
          awardStore.defaultProgramRef.programId,
          +emailTemplateId,
          createOrEditEmailTemplatePageUiStore.emailTemplateRequestBody,
          true);
        await uploadEmailTemplateAttachments(+emailTemplateId);
        await upsertEmailTemplateAttachments(+emailTemplateId);
        await apiStore.sendEmailTemplate(instance, awardStore.defaultProgramRef.programId, +emailTemplateId);
        navigate(`${RoutePaths.Email}`, {
          state: {
            operationType: 'send',
          } as Required<EmailPageRouteStates>
        });
      } else {
        if (!awardStore.defaultProgramRef) return;
        await uploadEmailTemplateBannerImage();
        const newEmailTemplateId = await apiStore.createEmailTemplate(
          instance,
          awardStore.defaultProgramRef.programId,
          createOrEditEmailTemplatePageUiStore.emailTemplateRequestBody,
          true,
          true);
        await uploadEmailTemplateAttachments(newEmailTemplateId);
        await upsertEmailTemplateAttachments(newEmailTemplateId);
        await apiStore.sendEmailTemplate(instance, awardStore.defaultProgramRef.programId, newEmailTemplateId);
        navigate(`${RoutePaths.Email}`, {
          state: {
            operationType: 'send',
          } as Required<EmailPageRouteStates>
        });
      }
    } catch (error) {
      // TODO - Handle error
      if (emailTemplateId) {
        setCreateOrEditEmailTemplateErrorMessage('Fail to update and send an email template.');
      } else {
        setCreateOrEditEmailTemplateErrorMessage('Fail to create and send an email template.');
      }
    } finally {
      setLoading(false);
      setIsShowSendEmailModal(false);
    }
  };

  const preloadData = async () => {
    setLoading(true);
    await getParamAndLoadEmailTemplate();
    setLoading(false);
  };

  useEffect(() => {
    preloadData();
    return () => {
      createOrEditEmailTemplatePageUiStore.resetStates();
    };
  }, []);

  return (
    <FlexBox
      isFullWidth
      direction='column'
      as='main'
      className='create-or-edit-email-template-page'
    >
      {
        isLoading && <Loading />
      }
      {
        createOrEditEmailTemplateErrorMessage && (
          <CustomMessageBar
            message={createOrEditEmailTemplateErrorMessage}
            notificationTypeCode='error'
            showDismissButton
          />
        )
      }
      {
        isShowSendEmailModal && (
          <SendEmailConfirmationModal
            isOpen={isShowSendEmailModal}
            onBackButtonClicked={() => setIsShowSendEmailModal(false)}
            onConfirmButtonClicked={() => onConfirmSendEmailButtonClicked()}
          />
        )
      }
      <PageTitle
        title={emailTemplateId ? 'Edit email' : 'New email'}
        subtitle={awardStore.defaultProgramRef ? awardStore.defaultProgramRef.displayName : ''}
      />
      <FlexBox
        direction='column'
        isFullWidth
        as='section'
      >
        <FormInput
          validationMessage={
            createOrEditEmailTemplatePageUiStore.isBeingValidated
            && createOrEditEmailTemplatePageUiStore.emailTemplateRequestValidation.fieldErrors.title?.length
              ? (
                <ValidationMessageList
                  messages={createOrEditEmailTemplatePageUiStore.emailTemplateRequestValidation.fieldErrors.title}
                />
              )
              : undefined
          }
          onChange={(newEmailTitle) => createOrEditEmailTemplatePageUiStore.setEmailTemplateRequestBody<string>(
            { key: 'title', value: newEmailTitle })
          }
          inputProps={{
            type: 'text',
            maxLength: 100,
            value: createOrEditEmailTemplatePageUiStore.emailTemplateRequestBody?.title || '',
          }}
          label='Email title'
          hintMessage={{
            children: `${createOrEditEmailTemplatePageUiStore.charsLength.title} / 100`,
            className: commonStyles.inputCharCounterHint,
          }}
        />
        <RecipientGroupDropdownInput />
        <IndividualRecipientInput />
        <BannerImageSection />
        <AttachmentsSection />
        <BodyMessageSection />
        <SendOnSection />
        <FlexBox
          className='section-buttons'
          isFullWidth
          content='end'
        >
          <Button
            onClick={() => onCancelButtonClicked()}
            appearance='secondary'
          >
            Cancel
          </Button>
          <Button
            onClick={() => onSaveAsTemplateButtonClicked()}
            appearance='secondary'
          >
            {
              createOrEditEmailTemplatePageUiStore.operationType === 'create' && 'Save as new template'
              || createOrEditEmailTemplatePageUiStore.operationType === 'update' && 'Update template'
            }
          </Button>
          <Button
            onClick={() => onSendButtonClicked()}
            appearance='primary'
          >
            Send
          </Button>
        </FlexBox>
      </FlexBox>
    </FlexBox>
  );
}

export const CreateOrEditEmailTemplatePage = observer(CreateOrEditEmailTemplatePageImpl);
